import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MailQuestion, Library, Send } from "lucide-react";
import { api } from "../api";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";

export function Footer() {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error(t("footer.newsletter.invalidEmail")); return; }
    setSending(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data?.message || t("footer.newsletter.successDefault"));
      setEmail("");
    } catch {
      toast.error(t("footer.newsletter.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="relative border-t border-cyan-500/10 bg-slate-950/85 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
                <Library className="h-5 w-5 text-white" />
              </div>
              <span className="font-display text-xl font-extrabold"><span className="gradient-text">DigiLib</span><span className="text-slate-400">.store</span></span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              {t("footer.tagline")}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {[["visa", "Visa"], ["mastercard", "Mastercard"], ["blik", "BLIK"], ["applepay", "Apple Pay"], ["googlepay", "Google Pay"], ["revolut", "Revolut Pay"], ["klarna", "Klarna"]].map(([f, alt]) => (
                <img key={f} src={`/img/pay/${f}.svg`} alt={alt} loading="lazy" className="h-6 w-auto rounded" />
              ))}
            </div>
            {lang !== "pl" && (
              <p className="mt-4 text-xs italic leading-relaxed text-slate-500">{t("footer.contentNotice")}</p>
            )}
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wider text-slate-200">{t("footer.shop")}</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
              <li><Link to="/" className="transition-colors hover:text-cyan-300">{t("footer.home")}</Link></li>
              <li><Link to="/blog" className="transition-colors hover:text-cyan-300">{t("footer.blog")}</Link></li>
              <li><a href="/#bestsellery" className="transition-colors hover:text-cyan-300">{t("footer.bestsellers")}</a></li>
              <li><a href="/#audiobooki" className="transition-colors hover:text-cyan-300">{t("footer.audiobooks")}</a></li>
              <li><a href="/bony" className="transition-colors hover:text-cyan-300" data-testid="footer-giftcards">{t("footer.giftcards")}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wider text-slate-200">{t("footer.info")}</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
              <li><Link to="/o-nas" data-testid="footer-about-link" className="font-bold gradient-text transition-opacity hover:opacity-75">{t("footer.about")}</Link></li>
              <li><Link to="/kontakt" data-testid="footer-contact-link" className="transition-colors hover:text-cyan-300">{t("footer.contact")}</Link></li>
              <li><Link to="/opinie" data-testid="footer-reviews-link" className="transition-colors hover:text-cyan-300">{t("footer.reviews")}</Link></li>
              <li>
                <button
                  data-testid="footer-book-request"
                  onClick={() => window.dispatchEvent(new Event("open-book-request"))}
                  className="flex items-center gap-1.5 font-bold gradient-text transition-opacity hover:opacity-75"
                >
                  <MailQuestion className="h-4 w-4 text-fuchsia-400" /> {t("footer.bookRequest")}
                </button>
              </li>
              <li><Link to="/regulamin" className="transition-colors hover:text-cyan-300">{t("footer.terms")}</Link></li>
              <li><Link to="/polityka-prywatnosci" className="transition-colors hover:text-cyan-300">{t("footer.privacy")}</Link></li>
              <li><Link to="/zwroty" data-testid="footer-returns-link" className="transition-colors hover:text-cyan-300">{t("footer.returns")}</Link></li>
              <li><Link to="/wysylka" className="transition-colors hover:text-cyan-300">{t("footer.shipping")}</Link></li>
              <li><Link to="/platnosci" className="transition-colors hover:text-cyan-300">{t("footer.payments")}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wider text-slate-200">{t("footer.newsletter")}</h4>
            <p className="mt-4 text-sm text-slate-400">{t("footer.newsletter.text.before")} <b className="text-cyan-300">{t("footer.newsletter.discount")}</b> {t("footer.newsletter.text.after")}</p>
            <form onSubmit={subscribe} className="mt-4 flex gap-2" data-testid="newsletter-form">
              <input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.newsletter.placeholder")}
                className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors focus:border-cyan-500"
              />
              <button
                data-testid="newsletter-submit-button"
                disabled={sending}
                className="rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 p-2.5 text-slate-950 transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
                aria-label={t("footer.newsletter.submit.aria")}
              >
                <Send className="h-5 w-5" />
              </button>
            </form>
            <p className="mt-2 text-[11px] leading-snug text-slate-500">{t("footer.newsletter.consent")} <Link to="/polityka-prywatnosci" className="underline hover:text-cyan-300">{t("footer.privacy")}</Link>.</p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-slate-800 pt-8 text-xs text-slate-500 sm:flex-row">
          <span>{t("footer.rights")}</span>
          <button onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))} data-testid="cookie-settings-link" className="transition-colors hover:text-cyan-300">{t("footer.cookieSettings")}</button>
          <span className="text-center sm:text-right">{t("footer.companyLine")} · <a href="mailto:kontakt@digilib.store" className="hover:text-cyan-300">kontakt@digilib.store</a></span>
        </div>
      </div>
    </footer>
  );
}
